import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useLang } from "@/i18n/LanguageContext";
import { fetchList } from "@/lib/api";
import NewsCard from "@/components/NewsCard";
import SeoHead from "@/components/SeoHead";
import PageHero from "@/components/PageHero";
import { topicBySlug, TOPICS } from "@/lib/topics";
import { articlePath } from "@/lib/radarPaths";
import { withDistinctImages } from "@/lib/imagePicker";
import { Rss } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Optional override: more specific H1 for SEO when the short label alone is
// ambiguous (e.g. "Planning" → "Supply Chain Planning"). Falls back to the
// taxonomy label when no override is set.
const H1_OVERRIDE = {
  planning: { en: "Supply Chain Planning", fr: "Planification Supply Chain" },
  inventory: { en: "Inventory Optimization", fr: "Optimisation des stocks" },
  manufacturing: { en: "Manufacturing Operations", fr: "Opérations industrielles" },
  "sourcing-procurement": { en: "Sourcing & Procurement", fr: "Achats & Sourcing" },
  "warehousing-logistics": { en: "Warehousing & Logistics", fr: "Entrepôts & Logistique" },
  "it-ai-governance": { en: "IT & AI Governance", fr: "Gouvernance IT & IA" },
  "sop-ibp": { en: "S&OP & IBP", fr: "S&OP & IBP" },
  "ai-in-supply-chain": { en: "AI in Supply Chain", fr: "IA en Supply Chain" },
};

const Topic = () => {
  const { slug } = useParams();
  const { lang } = useLang();
  const topic = topicBySlug(slug);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!topic) return;
    setLoading(true);
    fetchList("/news", { language: lang, category: topic.en, limit: 100 })
      .then((r) => { setItems(r); setLoading(false); })
      .catch(() => setLoading(false));
  }, [topic, lang]);

  if (!topic) return <Navigate to="/radar/news" replace />;

  const shortTitle = lang === "fr" ? topic.fr : topic.en;
  const h1Title = (H1_OVERRIDE[topic.slug]?.[lang]) || shortTitle;
  const desc = lang === "fr" ? topic.descFr : topic.descEn;
  const path = `/topic/${topic.slug}`;
  const canonical = `${BACKEND || ""}${path}`;
  const rssUrl = `${BACKEND}/api/rss/topic/${topic.slug}.xml`;

  // CollectionPage + ItemList JSON-LD for the topic landing page.
  const SITE = "https://dataleo.com";
  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${h1Title} - Dataleo Radar`,
    description: desc,
    inLanguage: lang,
    url: `${SITE}${path}${lang === "fr" ? "?lang=fr" : ""}`,
    isPartOf: { "@type": "WebSite", name: "Dataleo Supply Chain AI Radar", url: SITE },
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: items.length,
      itemListElement: items.slice(0, 20).map((n, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `${SITE}${articlePath("news", n)}`,
        name: n.title,
      })),
    },
  };

  return (
    <div data-testid={`topic-page-${topic.slug}`}>
      <SeoHead
        lang={lang}
        path={path}
        title={h1Title}
        description={desc}
        jsonLd={collectionSchema}
      />
      <Helmet>
        <link rel="alternate" type="application/rss+xml" title={`${h1Title} - Dataleo RSS`} href={rssUrl} />
      </Helmet>

      <PageHero
        kicker={(lang === "fr" ? "Thème · " : "Topic · ") + shortTitle}
        title={h1Title}
        description={desc}
        accent="green"
        testId={`topic-hero-${topic.slug}`}
        right={(
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="font-mono-dl text-[11px] uppercase tracking-wider text-[#D1D5DB]">
              <span className="text-white text-[18px] font-display leading-none">{items.length}</span>{" "}
              {lang === "fr" ? "articles" : "items"}
            </div>
            <a
              data-testid="topic-rss-link"
              href={rssUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-[#374151] hover:border-[#00A878] text-white text-[12px]"
            >
              <Rss size={12} /> RSS
            </a>
          </div>
        )}
      />

      <div className="dl-container py-10">

      {loading ? (
        <div className="text-[#4B5563]">…</div>
      ) : items.length === 0 ? (
        <div data-testid="topic-empty" className="text-[#4B5563] py-12 text-center">
          {lang === "fr" ? "Aucun article pour ce thème." : "No items for this topic yet."}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6" data-testid="topic-grid">
          {withDistinctImages(items, "news").map((n, i) => <NewsCard key={n.id} item={n} idx={i} />)}
        </div>
      )}

      {/* Cross-link to other topics for SEO discovery */}
      <div className="mt-16 border-t border-[#E5E7EB] pt-8">
        <div className="dl-overline text-[#00A878] mb-3">
          {lang === "fr" ? "Autres thèmes" : "Other topics"}
        </div>
        <div className="flex flex-wrap gap-2">
          {TOPICS.filter((x) => x.slug !== topic.slug).map((x) => (
            <Link
              key={x.slug}
              to={`/topic/${x.slug}`}
              data-testid={`topic-link-${x.slug}`}
              className="px-3 py-1.5 bg-white border border-[#E5E7EB] hover:border-[#111827] text-[13px] transition-colors"
            >
              {lang === "fr" ? x.fr : x.en}
            </Link>
          ))}
        </div>
      </div>
      </div>
    </div>
  );
};

export default Topic;
